import { useEffect, useRef, useState } from "react";
import { useAuthContext } from "../../contexts/auth-context";
import { useServiceContext } from "../../contexts/service";
import { TChatListResponse, TChatListRow } from "../../lib/types/chats";
import { useFileManagement } from "../../services/file-management";
import { getCacheKillQueryString } from "../../lib/common/api";
import { ASSETS_ROUTES } from "../../lib/common/constants";
import { Spinner } from "../common/spinner";
import { TProcessStates } from "../../lib/types/general";
import { OneConversationSubMenu } from "./chat-menu";
import { useNavigate } from "react-router-dom";
import { TChatSections } from "./chat-types";
import { LogoTech } from "../logo";
import { TApiTechnician } from "../../lib/types/dentalTechs";
import { LogoLab } from "../logo-lab";
import { TApiLab } from "../../lib/types/labs";

type TChatListRowProps = {
    chatInfo: TChatListRow;
    masterKey: string;
    fullscreen: boolean;
    conversationArchivedHandler: (conversation_id: number) => void;
    conversationUnArchivedHandler: (conversation_id: number) => void;
};

export const ChatListRow = ({ chatInfo, masterKey, fullscreen, conversationArchivedHandler, conversationUnArchivedHandler }: TChatListRowProps) => {
    const { getImagePath } = useFileManagement();
    const {
        chats: { addUserIdToChat, startConversation },
    } = useServiceContext();

    const [imgLogo, setImgLogo] = useState<string>("");
    const [isHovered, setIsHovered] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const fallbackImage = chatInfo.user_type === "lab" ? ASSETS_ROUTES.IMGS.labAvatarUrl : ASSETS_ROUTES.IMGS.techAvatarUrl;
    const logoCustomizationStyle: React.CSSProperties = chatInfo.logo_customization
        ? {
              backgroundSize: `${chatInfo.logo_customization.percentageSize}%`,
              backgroundPositionX: `${chatInfo.logo_customization.left}%`,
              backgroundPositionY: `${chatInfo.logo_customization.top}%`,
              backgroundRepeat: "no-repeat",
          }
        : {
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
          };

    const openSelectedChat = (e: React.MouseEvent) => {
        e.preventDefault();
        addUserIdToChat({
            id: chatInfo.chat_with_user_id,
            name: chatInfo.name,
        });
        startConversation(chatInfo.chat_with_user_id);
    };

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    const chatDate = new Date(chatInfo.sent_date);
    chatDate.setHours(0, 0, 0, 0);

    const displayDate =
        chatDate.getTime() === today.getTime() ? "Today" : chatDate.getTime() === yesterday.getTime() ? "Yesterday" : chatDate.toLocaleDateString();

    useEffect(() => {
        setImgLogo(chatInfo.from_user_logo ? getImagePath(chatInfo.from_user_logo, "fixed", chatInfo.guid) + "?" + getCacheKillQueryString() : fallbackImage);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div
            key={`${masterKey}_conversation_${chatInfo.conversation_id}`}
            onClick={!menuOpen ? openSelectedChat : undefined}
            className={`list-group-item list-group-item-action ${chatInfo.unread_message_from_sender ? "chatListBox__unread-msg" : ""}`}
            role="button"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => {
                setIsHovered(false);
                setMenuOpen(false);
            }}
        >
            <div className="row">
                <div className="col-2">
                    {!!imgLogo && (
                        <div
                            className="rounded-circle shadow chatListBox__chatsLogo"
                            style={{ backgroundImage: `url(${imgLogo})`, ...logoCustomizationStyle }}
                        />
                    )}
                </div>
                <div className="col-10 ps-4">
                    <div className="row">
                        <div className="col-md-8 col-7">
                            <p className="fw-bold m-0 text-truncate">{chatInfo.name}</p>
                        </div>
                        <div className="col-md-4 col-5 hstack align-items-start">
                            {!fullscreen && isHovered ? (
                                <div className="chatSubMenu">
                                    <button
                                        className="btn btn-sm btn-light rounded-circle"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setMenuOpen((prev) => !prev);
                                        }}
                                    >
                                        <i className="fa-solid fa-ellipsis"></i>
                                    </button>
                                </div>
                            ) : (
                                <p className="m-0">
                                    <small style={{ fontSize: "smaller" }}>{displayDate}</small>
                                </p>
                            )}
                            {fullscreen && (
                                <>
                                    <div className="ms-auto">
                                        <button
                                            className="btn btn-sm btn-light rounded-circle"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setMenuOpen((prev) => !prev);
                                            }}
                                        >
                                            <i className="fa-solid fa-ellipsis"></i>
                                        </button>
                                    </div>
                                </>
                            )}

                            {menuOpen && (
                                <OneConversationSubMenu
                                    chatRowData={chatInfo}
                                    onCloseMenu={() => {
                                        setMenuOpen(false);
                                    }}
                                    conversationArchivedHandler={conversationArchivedHandler}
                                    conversationUnArchivedHandler={conversationUnArchivedHandler}
                                    // deleteCallback={() => {
                                    // }}
                                />
                            )}
                        </div>
                    </div>
                    <div className="row">
                        <p className="m-0 text-truncate chatListBox__message-line">
                            {chatInfo.last_message_from === chatInfo.chat_with_user_id ? chatInfo.name : "You"}: {chatInfo.content}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

type TChatListProps = {
    fullScreenMode?: boolean;
};
export const ChatList = ({ fullScreenMode = false }: TChatListProps) => {
    const { userInfo } = useAuthContext();
    const [chevronUp, setChevronUp] = useState(true);
    const [minimized, setMinimized] = useState(fullScreenMode ? false : true);
    const [newUnreadConversationsCount, setNewUnreadConversationsCount] = useState<number>(0);
    const [newUnreadArchivedConversationsCount, setNewUnreadArchivedConversationsCount] = useState<number>(0);
    const {
        chats: { getChatList, listUserIdToChat, addOpenCloseMessageCenterHandler, removeOpenCloseMessageCenterHandler },
    } = useServiceContext();

    const [chatList, setChatList] = useState<TChatListResponse>({
        data: { data: [], totalCount: 0, unreadTotalCount: 0 },
    });
    const pageNumberRef = useRef(0);
    const loadingMoreRef = useRef(false);
    const [workingState, setWorkingState] = useState<TProcessStates>("idle");
    const [hasMore, setHasMore] = useState(true);
    const chatListScrollRef = useRef<HTMLDivElement | null>(null);
    const endOfChatListRef = useRef<HTMLDivElement | null>(null);
    const [selectedChatSection, _setSelectedChatSection] = useState<TChatSections>("Inbox");

    const isUserEnabled = !!userInfo?.enabled;

    const setSelectedChatSection = (section: TChatSections) => {
        window.wwgChatSelectedSection = section;
        _setSelectedChatSection(section);
    };
    const navigateTo = useNavigate();

    const openCloseMessageHandler = () => {
        if (minimized) loadInitialChats();
        setMinimized((prev) => {
            window.wwgChatListIsMinimized = !prev;
            return !prev;
        });
        setChevronUp((prev) => !prev);
    };

    const onClickMinimizeChatHandler = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (fullScreenMode) return;
        openCloseMessageHandler();
    };

    const loadInitialChats = async () => {
        if (!userInfo) return;
        if (workingState === "working") return;

        setWorkingState("working");

        const data: TChatListResponse = await getChatList(userInfo.id, 0, false);
        const newChats = data?.data?.data ?? [];
        const totalCount = data.data.totalCount ?? 0;
        setNewUnreadConversationsCount(data.data.unreadTotalCount);

        const archivedData = await getChatList(userInfo.id, 0, true);
        const totalArchivedCount = archivedData.data.totalCount ?? 0;
        const newArchivedChats = archivedData?.data?.data ?? [];
        setNewUnreadArchivedConversationsCount(archivedData.data.unreadTotalCount);

        if (window.wwgChatSelectedSection === "Archive") {
            setChatList({
                data: {
                    data: newArchivedChats,
                    totalCount: totalArchivedCount,
                    unreadTotalCount: archivedData.data.unreadTotalCount,
                },
            });
            window.wwgChatListHasMoreChats = newArchivedChats.length < totalArchivedCount;
        } else {
            setChatList({
                data: {
                    data: newChats,
                    totalCount,
                    unreadTotalCount: data.data.unreadTotalCount,
                },
            });
            window.wwgChatListHasMoreChats = newChats.length < totalCount;
        }

        setHasMore(window.wwgChatListHasMoreChats);

        pageNumberRef.current = 0;

        setWorkingState("done");
    };

    const refreshInboxSection = async () => {
        if (!userInfo) return;

        setWorkingState("working");

        const inboxList: TChatListResponse = await getChatList(userInfo.id, 0, false);
        setChatList(inboxList);
        setNewUnreadConversationsCount(inboxList.data.unreadTotalCount);
        pageNumberRef.current = 0;
        const inboxHasMore = inboxList.data.data.length < inboxList.data.totalCount;
        setHasMore(inboxHasMore);
        window.wwgChatListHasMoreChats = inboxHasMore;
        setWorkingState("done");
    };
    const onInboxSectionClickHandler = async () => {
        if (selectedChatSection === "Inbox") return;
        setSelectedChatSection("Inbox");
        await refreshInboxSection();
    };

    const refreshArchivedSection = async () => {
        if (!userInfo) return;

        setWorkingState("working");

        const archiveList: TChatListResponse = await getChatList(userInfo.id, 0, true);
        setChatList(archiveList);
        setNewUnreadArchivedConversationsCount(archiveList.data.unreadTotalCount);
        pageNumberRef.current = 0;
        const archiveHasMore = archiveList.data.data.length < archiveList.data.totalCount;
        setHasMore(archiveHasMore);
        window.wwgChatListHasMoreChats = archiveHasMore;
        setWorkingState("done");
    };
    const onArchivedSectionClickHandler = async () => {
        if (selectedChatSection === "Archive") return;
        setSelectedChatSection("Archive");

        await refreshArchivedSection();
    };

    const {
        notifications: { addNewMessagesHandler, removeMessageHandler, addNewConversationMessageHandler, removeNewConversationMessageHandler },
    } = useServiceContext();

    const goBack = () => {
        navigateTo(-1);
    };

    useEffect(() => {
        if (!isUserEnabled || !userInfo?.id) return;
        window.wwgChatListHasMoreChats = true;
        window.wwgChatListIsMinimized = true;
        window.wwgChatSelectedSection = "Inbox";

        //-------setting the update behavior

        addNewMessagesHandler(loadInitialChats);
        addNewConversationMessageHandler(loadInitialChats);
        addOpenCloseMessageCenterHandler(openCloseMessageHandler);
        // //------initial draw behavior

        if (!minimized) loadInitialChats();

        return () => {
            removeOpenCloseMessageCenterHandler(openCloseMessageHandler);
            removeMessageHandler(loadInitialChats);
            removeNewConversationMessageHandler(loadInitialChats);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const scrollContainer = chatListScrollRef.current;
        const endOfList = endOfChatListRef.current;

        if (minimized || workingState !== "done" || !hasMore || !userInfo || !scrollContainer || !endOfList) return;

        const loadMoreChats = async () => {
            if (loadingMoreRef.current) return;

            loadingMoreRef.current = true;
            setWorkingState("working");

            const requestedSection = window.wwgChatSelectedSection;
            const nextPageNumber = pageNumberRef.current + 1;

            try {
                const data: TChatListResponse = await getChatList(userInfo.id, nextPageNumber, requestedSection === "Archive");

                if (window.wwgChatSelectedSection !== requestedSection) return;

                const newChats = data?.data?.data ?? [];
                const newChatsTotalCount = data.data.unreadTotalCount;

                if (requestedSection === "Archive") {
                    setNewUnreadArchivedConversationsCount(newChatsTotalCount);
                } else {
                    setNewUnreadConversationsCount(newChatsTotalCount);
                }

                setChatList((prev) => {
                    const totalCount = data.data.totalCount ?? prev.data.totalCount;
                    const existingConversationIds = new Set(prev.data.data.map((chat) => chat.conversation_id));
                    const uniqueNewChats = newChats.filter((chat) => !existingConversationIds.has(chat.conversation_id));
                    const updatedList = [...prev.data.data, ...uniqueNewChats];
                    const listHasMore = newChats.length > 0 && updatedList.length < totalCount;

                    setHasMore(listHasMore);
                    window.wwgChatListHasMoreChats = listHasMore;

                    return {
                        data: {
                            data: updatedList,
                            totalCount,
                            unreadTotalCount: data.data.unreadTotalCount,
                        },
                    };
                });
                pageNumberRef.current = nextPageNumber;
            } finally {
                loadingMoreRef.current = false;
                setWorkingState("done");
            }
        };

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) loadMoreChats();
            },
            { root: scrollContainer, threshold: 0 },
        );

        observer.observe(endOfList);

        return () => observer.disconnect();
    }, [getChatList, hasMore, minimized, userInfo, workingState]);

    if (!userInfo || !isUserEnabled) return null;

    return (
        <>
            {(!fullScreenMode || (fullScreenMode && listUserIdToChat.length === 0)) && (
                <div className="chatListBox">
                    <div className="container bg-light p-0 shadow-lg rounded-top-3">
                        <div className="chatListTitle  hstack bg-secondary rounded-top-3 text-white" role="button" onClick={onClickMinimizeChatHandler}>
                            {fullScreenMode && (
                                <button className="btn border-0 text-white p-0 rounded-3 ms-3 chatTitle__iconButton" onClick={goBack} aria-label="Back to conversations">
                                    <i className="fa-chevron-left fa-solid"></i>
                                </button>
                            )}
                            <div className="p-1 ms-2 hstack">
                                {userInfo.type === "tech" && (
                                    <LogoTech
                                        editable={false}
                                        userInfo={userInfo as unknown as TApiTechnician}
                                        extraClass="rounded-circle shadow chatListBox__logo me-3"
                                        isSomePartBeingEdited={false}
                                        setInappropriateContent={() => {}}
                                        setUnsupportedImageType={() => {}}
                                        workingState={"done"}
                                    />
                                )}
                                {userInfo.type === "lab" && (
                                    <LogoLab
                                        editable={false}
                                        userInfo={userInfo as unknown as TApiLab}
                                        extraClass="rounded-circle shadow chatListBox__logo me-3"
                                        isSomePartBeingEdited={false}
                                        setInappropriateContent={() => {}}
                                        setUnsupportedImageType={() => {}}
                                        workingState={"done"}
                                    />
                                )}

                                <h6 className="m-0 fw-bold">Messaging</h6>
                                {minimized && newUnreadConversationsCount > 0 && <span className="badge bg-primary ms-2">{newUnreadConversationsCount}</span>}
                            </div>
                            {!fullScreenMode && <i className={`fa-solid p-3 ms-auto ${chevronUp ? "fa-chevron-up" : "fa-chevron-down"}`}></i>}
                        </div>
                        <div
                            ref={chatListScrollRef}
                            className={`list-group list-group-flush chatListBox__scrollable bg-light ${minimized ? "d-none" : "d-block"}`}
                        >
                            {!minimized && (
                                <>
                                    <div className="p-2" style={{ fontSize: "12px" }}>
                                        <button
                                            type="button"
                                            className={`btn ${selectedChatSection === "Inbox" ? "btn-success" : "btn-light"} btn-sm me-2 `}
                                            onClick={() => onInboxSectionClickHandler()}
                                        >
                                            Inbox{" "}
                                            {newUnreadConversationsCount > 0 && <span className="badge text-bg-secondary">{newUnreadConversationsCount}</span>}
                                        </button>
                                        |
                                        <button
                                            type="button"
                                            className={`btn ${selectedChatSection === "Archive" ? "btn-success" : "btn-light"} btn-sm ms-2`}
                                            onClick={() => onArchivedSectionClickHandler()}
                                        >
                                            Archive{" "}
                                            {newUnreadArchivedConversationsCount > 0 && (
                                                <span className="badge text-bg-secondary">{newUnreadArchivedConversationsCount}</span>
                                            )}
                                        </button>
                                    </div>
                                    {chatList?.data.data.length > 0 ? (
                                        chatList?.data.data.map((chat, index) => (
                                            <ChatListRow
                                                key={`chat_row_with_user_id_${chat.chat_with_user_id}_${index}`}
                                                chatInfo={chat}
                                                masterKey="chat-list_row"
                                                fullscreen={fullScreenMode}
                                                conversationArchivedHandler={() => {
                                                    refreshInboxSection();
                                                }}
                                                conversationUnArchivedHandler={() => {
                                                    refreshArchivedSection();
                                                }}
                                            />
                                        ))
                                    ) : (
                                        <p className="text-center p-3 m-0">No chats found</p>
                                    )}
                                    {workingState === "working" && (
                                        <div className="text-center p-3 m-0">
                                            <Spinner spinnerColor="text-primary" />
                                        </div>
                                    )}
                                    {!hasMore && workingState === "done" && (
                                        <div>
                                            <p className="text-center p-3 m-0">No more chats found</p>
                                        </div>
                                    )}
                                </>
                            )}
                            <div ref={endOfChatListRef} id="end-of-chatList" />
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};
